import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import WeekCalendar from '../week-calendar';
import { message, Button, Select } from 'antd';
import { apiBaseUrl, serverFetch } from '../../server';
import { useWindowWidth } from '@react-hook/window-size';
import './calendar.css';
import { CalendarDescription } from './calendar-descr';

const emptyItems = [];
const SELECT_CHROME_WIDTH = 72;
const URL_SOTR_KEYS = ['SotrId', 'sotrId', 'sotr'];
const URL_ROOM_KEYS = ['RoomId', 'roomId'];

const measureMaxLabelWidth = (labels) => {
    if (typeof document === 'undefined') return 0;
    const canvas = measureMaxLabelWidth.canvas || (measureMaxLabelWidth.canvas = document.createElement('canvas'));
    const ctx = canvas.getContext('2d');
    ctx.font = '14px Oswald, Arial, sans-serif';
    return labels.reduce((max, label) => Math.max(max, ctx.measureText(String(label || '')).width), 0);
};

const getSelectWidth = (options, placeholder) => {
    const labels = options.map((option) => option.label).concat(placeholder);
    return Math.ceil(measureMaxLabelWidth(labels) + SELECT_CHROME_WIDTH);
};

const parseIdParam = (value) => {
    if (value == null || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const Calendar = () => {
    dayjs.locale('ru');
    const history = useHistory();
    const location = useLocation();
    const user = useSelector((s) => s.user);
    const company = useSelector((s) => s.company);
    const [items, setItems] = useState(emptyItems);
    const [startDate, setStartDate] = useState(
        dayjs(
            moment()
                .utcOffset(0, true)
                .set({ hour: 24, minute: 0, second: 0, millisecond: 0 })
                .isoWeekday(1)
                .utcOffset(0, true)
        )
    );
    const [dayPos, setDayPos] = useState(dayjs().diff(startDate, 'day'));
    const [rooms, setRooms] = useState([]);
    const [sotrs, setSotrs] = useState([]);
    const [selectedRoomId, setSelectedRoomId] = useState();
    const [selectedSotrId, setSelectedSotrId] = useState();
    const [selectedUsl, setSelectedUsl] = useState();
    const onlyWidth = useWindowWidth();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        let changed = false;
        let sotrId;
        let roomId;

        for (const key of URL_SOTR_KEYS) {
            if (searchParams.has(key)) {
                if (sotrId == null) sotrId = parseIdParam(searchParams.get(key));
                searchParams.delete(key);
                changed = true;
            }
        }
        for (const key of URL_ROOM_KEYS) {
            if (searchParams.has(key)) {
                if (roomId == null) roomId = parseIdParam(searchParams.get(key));
                searchParams.delete(key);
                changed = true;
            }
        }

        if (sotrId != null) setSelectedSotrId(sotrId);
        if (roomId != null) setSelectedRoomId(roomId);

        if (changed) {
            const search = searchParams.toString();
            history.replace({
                pathname: location.pathname,
                search: search ? `?${search}` : '',
                hash: location.hash,
            });
        }
    }, []);

    const getFilters = () => {
        if (!company?.id) return;
        serverFetch('room', { method: 'GET', queryParams: { companyId: company.id } }, user)
            .then((data) => {
                setRooms(
                    (data || [])
                        .filter((item) => item.isActive !== false)
                        .map((item) => ({ id: item.id, name: item.name }))
                );
            })
            .catch(() => {
                message.error('Ошибка получения объектов');
            });
        serverFetch('sotr', { method: 'GET', queryParams: { companyId: company.id } }, user)
            .then((data) => {
                setSotrs((data || []).map((item) => ({ id: item.id, name: item.name })));
            })
            .catch(() => {
                message.error('Ошибка получения сотрудников');
            });
    };

    const getItems = () => {
        if (!company?.id) return;
        let queryParams = {
            companyId: company.id,
            start: startDate,
            finish: startDate.add(6, 'day'),
        };
        serverFetch('Room/seanses', { method: 'GET', queryParams: queryParams }, user)
            .then((data) => {
                setItems(data);
            })
            .catch(() => {
                message.error('Ошибка получения сеансов');
            });
    };

    useEffect(() => getFilters(), [company]);
    useEffect(() => getItems(), [company, startDate]);

    const onSetToday = () => {
        const newStartDate = dayjs(
            moment()
                .utcOffset(0, true)
                .set({ hour: 24, minute: 0, second: 0, millisecond: 0 })
                .isoWeekday(1)
                .utcOffset(0, true)
        );
        setStartDate(newStartDate);
        setDayPos(dayjs().diff(newStartDate, 'day'));
    };

    const compact = onlyWidth < 745;

    const filteredItems = items.filter((item) => {
        if (selectedRoomId && selectedRoomId !== item.roomId) return false;
        if (selectedSotrId && selectedSotrId !== item.sotrId) return false;
        return true;
    });

    const roomOptions = rooms.map((i) => ({ label: i.name, value: i.id }));
    const sotrOptions = sotrs.map((i) => ({ label: i.name, value: i.id }));
    const roomSelectWidth = getSelectWidth(roomOptions, 'Зал');
    const sotrSelectWidth = getSelectWidth(sotrOptions, 'Сотрудник');
    const weekEnd = startDate.add(6, 'day');
    const sotrImageUrl = selectedUsl?.sotrImageId
        ? `${apiBaseUrl}/api/image/File/${selectedUsl.sotrImageId}`
        : null;

    return (
        <div className='block-content calendar-page'>
            {selectedUsl && (
                <CalendarDescription
                    onClose={() => setSelectedUsl(null)}
                    name={selectedUsl.productName || selectedUsl.comment}
                    description={selectedUsl.productDescription}
                    sotrName={selectedUsl.sotrName}
                    sotrDescription={selectedUsl.sotrDescription}
                    sotrImageUrl={sotrImageUrl}
                />
            )}
            <div className='calendar-toolbar'>
                <Button className='calendar-toolbar-today' onClick={onSetToday}>
                    Сегодня
                </Button>
                <div className='calendar-toolbar-range'>
                    <Button
                        className='calendar-toolbar-nav'
                        shape='circle'
                        icon={<LeftOutlined />}
                        onClick={() => setStartDate(startDate.add(-7, 'd'))}
                    />
                    <div className='calendar-toolbar-dates'>
                        {startDate.format('DD MMMM')} - {weekEnd.format('DD MMMM')}
                    </div>
                    <Button
                        className='calendar-toolbar-nav'
                        shape='circle'
                        icon={<RightOutlined />}
                        onClick={() => setStartDate(startDate.add(7, 'd'))}
                    />
                </div>
                <div className='calendar-toolbar-selects'>
                    {rooms && rooms.length > 0 && (
                        <Select
                            className='calendar-toolbar-select'
                            placeholder='Зал'
                            options={roomOptions}
                            value={selectedRoomId}
                            style={compact ? undefined : { width: roomSelectWidth }}
                            onChange={(e) => setSelectedRoomId(e)}
                            onClear={() => setSelectedRoomId()}
                            allowClear
                        />
                    )}
                    {sotrs && sotrs.length > 0 && (
                        <Select
                            className='calendar-toolbar-select'
                            placeholder='Сотрудник'
                            options={sotrOptions}
                            value={selectedSotrId}
                            style={compact ? undefined : { width: sotrSelectWidth }}
                            onChange={(e) => setSelectedSotrId(e)}
                            onClear={() => setSelectedSotrId()}
                            allowClear
                        />
                    )}
                </div>
            </div>
            <WeekCalendar
                items={filteredItems}
                startDate={startDate}
                startTime={company && company.startMin ? company.startMin : 420}
                finishTime={company && company.finishMin ? company.finishMin : 1200}
                compact={compact}
                onItemClick={(id) => setSelectedUsl(items.find((i) => i.id == id))}
                dayPos={dayPos}
                setDayPos={setDayPos}
            />
        </div>
    );
};

export default Calendar;
